// Client side of the paid AI features (receipt scanning, spending assistant, bill-negotiation drafts).
// The model key lives only in the ai-assist Edge Function; the browser sends the request there as the signed-in user.
import { supabase, syncConfigured } from "./supabaseClient.js";
import { detectBills } from "./billWatch.js";

export const UPGRADE_URL = "";   // where "Upgrade" should send people (Stripe Payment Link etc.); empty hides the button

async function call(body) {
  if (!syncConfigured) throw new Error("AI features need an online account. Open Settings, then Account & sync, and sign in.");
  const { data, error } = await supabase.functions.invoke("ai-assist", { body });
  if (error) {
    let msg = error.message || "Request failed";
    try { const j = await error.context?.json?.(); if (j?.error || j?.message) msg = j.error || j.message; } catch (e) { /* keep default */ }
    throw Object.assign(new Error(msg), { status: error.context?.status });
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

export const aiStatus = () => call({ mode: "status" });

// Shrink a phone photo before upload: long edge 1400px, JPEG. Keeps text readable but cuts the image's token cost a lot.
export async function fileToJpegBase64(file, maxDim = 1400, quality = 0.82) {
  let bmp;
  try { bmp = await createImageBitmap(file); }
  catch (e) {
    bmp = await new Promise((res, rej) => { const img = new Image(); img.onload = () => res(img); img.onerror = () => rej(new Error("Couldn't open that image. Try a JPEG or PNG photo.")); img.src = URL.createObjectURL(file); });
  }
  const w = bmp.width || bmp.naturalWidth, h = bmp.height || bmp.naturalHeight;
  const scale = Math.min(1, maxDim / Math.max(w, h));
  const c = document.createElement("canvas");
  c.width = Math.round(w * scale); c.height = Math.round(h * scale);
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(bmp, 0, 0, c.width, c.height);
  return c.toDataURL("image/jpeg", quality).split(",")[1];
}

const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export async function scanReceipt(file, { categories = [] } = {}) {
  if (file.size > 25 * 1024 * 1024) throw new Error("That photo is too large.");
  const image = await fileToJpegBase64(file);
  const data = await call({ mode: "receipt", image, categories, today: todayISO() });
  return data.receipt;
}

export async function askAssistant(messages, context, currency) {
  const data = await call({ mode: "chat", messages, context, currency });
  return data.reply;
}

export async function draftBillMessage(bill, { searchDeals = false } = {}) {
  return call({ mode: "bill", bill, searchDeals });
}

// A compact summary of the user's spending: totals, categories, merchants and bills. Not the raw expense list.
export function buildAssistantContext(profile, today = todayISO()) {
  const ex = (profile.expenses || []).filter((e) => e && Number(e.amount) > 0 && /^\d{4}-\d{2}-\d{2}$/.test(e.date || ""));
  const ym = today.slice(0, 7);
  const prev = (n) => { const [y, m] = ym.split("-").map(Number); const d = new Date(Date.UTC(y, m - 1 - n, 1)); return d.toISOString().slice(0, 7); };
  const months = [ym, prev(1), prev(2), prev(3)];
  const byMonthCat = {};
  months.forEach((m) => { byMonthCat[m] = {}; });
  const merchants = {};
  const cutoff = new Date(Date.parse(`${today}T00:00:00Z`) - 90 * 86400000).toISOString().slice(0, 10);
  ex.forEach((e) => {
    const m = e.date.slice(0, 7);
    if (byMonthCat[m]) byMonthCat[m][e.category || "Other"] = (byMonthCat[m][e.category || "Other"] || 0) + Number(e.amount);
    if (e.date >= cutoff && e.note) { const k = e.note.trim().slice(0, 40); merchants[k] = merchants[k] || { n: 0, total: 0 }; merchants[k].n++; merchants[k].total += Number(e.amount); }
  });
  const r = (n) => Math.round(n * 100) / 100;
  const monthly = months.map((m) => ({ month: m, total: r(Object.values(byMonthCat[m]).reduce((s, v) => s + v, 0)), byCategory: Object.fromEntries(Object.entries(byMonthCat[m]).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([k, v]) => [k, r(v)])) }));
  const topMerchants = Object.entries(merchants).sort((a, b) => b[1].total - a[1].total).slice(0, 15).map(([name, v]) => ({ name, times: v.n, total: r(v.total) }));
  const bills = detectBills(ex, { today }).filter((b) => b.active).slice(0, 12).map((b) => ({ name: b.label, every: b.cadence, amount: b.lastAmount, previous: b.previousAmount, increased: b.increased, since: b.increaseDate || undefined }));
  return {
    today, currency: profile.currency || "USD", profileType: profile.type, monthlyBudget: profile.budget || null,
    categoryBudgets: profile.categoryBudgets || {}, spendingByMonth: monthly, topMerchantsLast90Days: topMerchants, recurringBills: bills,
    recurringSetup: (profile.recurring || []).slice(0, 12).map((x) => ({ note: x.note, amount: x.amount, category: x.category })),
  };
}
